import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Component, useEffect, type ReactNode} from 'react'
import * as z from 'zod'

const RedirectTo500 = () => {
  useEffect(() => {
    window.location.assign('/500')
  })

  return null
}

type ErrorBoundaryProps = {children: ReactNode}
type ErrorBoundaryState = {hasError: boolean}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)

    this.state = {hasError: false}
  }

  static getDerivedStateFromError(error: Error) {
    if (error instanceof z.ZodError) {
      return {hasError: true}
    }

    return {hasError: false}
  }

  /**
   * When `react_centralized_error_handling` feature flag is enabled:
   * - Error reporting is handled centrally via React 19's `onCaughtError` callback
   *
   * When feature flag is disabled (legacy behavior):
   * - Re-throws the error out of the rendering context to propagate to Failbot
   */
  override componentDidCatch(error: Error) {
    if (!isFeatureEnabled('react_centralized_error_handling')) {
      // Legacy behavior: re-throw the error out of the rendering context
      // to ensure it propagates to failbot
      setTimeout(() => {
        throw error
      })
    }
    // When flag is enabled, React 19's onCaughtError handles reporting
  }

  override render() {
    if (this.state.hasError) {
      return <RedirectTo500 />
    }

    return this.props.children
  }
}

try{ RedirectTo500.displayName ||= 'RedirectTo500' } catch {}